import { Check } from "lucide-react";
import type { KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

export type ReviewChoiceOption<T extends string> = {
  value: T;
  label: string;
};

/**
 * Compact, explicit alternative to a select for small review decisions.
 * Every option stays visible, keyboard reachable, and unambiguous.
 */
export function ReviewChoiceGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
  className,
  optionClassName,
}: {
  label: string;
  value: T | null;
  options: ReadonlyArray<ReviewChoiceOption<T>>;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  optionClassName?: string;
}) {
  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    optionIndex: number,
  ) {
    if (disabled) return;
    let targetIndex: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        targetIndex = (optionIndex + 1) % options.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        targetIndex = (optionIndex - 1 + options.length) % options.length;
        break;
      case "Home":
        targetIndex = 0;
        break;
      case "End":
        targetIndex = options.length - 1;
        break;
      default:
        return;
    }
    const target = options[targetIndex];
    if (!target) return;
    event.preventDefault();
    onChange(target.value);
    const radios = event.currentTarget
      .closest('[role="radiogroup"]')
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    radios?.[targetIndex]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      className={cn(
        "inline-flex max-w-full flex-wrap gap-1 rounded-lg border bg-muted/30 p-1",
        className,
      )}
    >
      {options.map((option, optionIndex) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            tabIndex={disabled ? -1 : selected || (value === null && optionIndex === 0) ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, optionIndex)}
            className={cn(
              "inline-flex min-h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-default",
              selected && "bg-background text-foreground shadow-sm",
              disabled && !selected && "opacity-50",
              optionClassName,
            )}
          >
            {selected ? (
              <Check className="size-3.5" aria-hidden="true" />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
